import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuthStore, useSubscriptionStore } from "@/store";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  ArrowLeft,
  Crown,
  Check,
  Sparkles,
  Zap,
  ArrowRight,
} from "lucide-react";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import { translateCurrentPathText as t } from "@/lib/translations";

const planIcons = {
  starter: Sparkles,
  growth: Zap,
  pro: Crown,
  agency: Crown,
};

export default function SubscriptionPage() {
  const navigate = useLocalizedNavigation();
  const { user, checkAuth } = useAuthStore();
  const { plans, fetchPlans, createSubscription, captureSubscription, cancelSubscription, paypalClientId, fetchPayPalConfig, isLoading } = useSubscriptionStore();

  useEffect(() => {
    fetchPlans();
    fetchPayPalConfig();
  }, [fetchPlans, fetchPayPalConfig]);

  const currentPlan = plans.find((p) => p.id === user?.subscription_plan) || null;
  const exportProgress = user?.export_limit ? Math.round((user.exports_used / user.export_limit) * 100) : 0;
  const currentCycleLabel = String(currentPlan?.billing_interval_unit || "").toUpperCase() === "DAY" ? "day" : "month";
  const getPlanCycleLabel = (plan) => (String(plan?.billing_interval_unit || "").toUpperCase() === "DAY" ? "/day" : "/mo");

  const isCancelledPlan = user?.subscription_plan === "cancelled";

  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(t("Cancel your subscription at period end?"));
    if (!confirmed) return;

    const result = await cancelSubscription();
    if (result.success) {
      toast.success(t("Subscription cancellation scheduled. You keep access until the renewal date."));
      await checkAuth();
    } else {
      toast.error(result.error || "Failed to cancel subscription");
    }
  };

  const handleUpgrade = async (planId) => {
    const result = await createSubscription(planId);
    if (result?.subscription_id) {
      return result.subscription_id;
    }
    throw new Error("Failed to create subscription");
  };

  const handlePayPalApprove = async (data, planId) => {
    const result = await captureSubscription(data.subscriptionID);
    if (result) {
      toast.success("Subscription upgraded successfully!");
      await checkAuth();
    } else {
      toast.error("Payment failed. Please try again.");
    }
  };

  const subscriptionContent = (
    <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-semibold">Subscription</h1>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          {/* Current Plan */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4">Current Plan</h2>
            
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                currentPlan ? "bg-teal-100" : "bg-slate-100"
              }`}>
                {currentPlan && planIcons[currentPlan.id] && (
                  (() => {
                    const Icon = planIcons[currentPlan.id];
                    return <Icon className="w-7 h-7 text-teal-600" />;
                  })()
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold">{isCancelledPlan ? t("Cancelled") : t(currentPlan?.name || "No active")} Plan</h3>
                <p className="text-slate-600">
                  {isCancelledPlan ? "Your paid subscription ended." : currentPlan?.price ? `€${currentPlan.price}${getPlanCycleLabel(currentPlan)}` : "Choose a paid plan to start exporting"}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">{currentCycleLabel === "month" ? t("Exports this month") : `Exports this ${currentCycleLabel}`}</span>
                  <span className="font-medium">{user?.exports_used || 0} / {user?.export_limit || 0}</span>
                </div>
                <Progress value={exportProgress} className="h-2" />
              </div>

              <div className="flex flex-wrap gap-2">
                {currentPlan?.features?.map((feature, idx) => (
                  <span key={idx} className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-600">
                    {t(feature)}
                  </span>
                ))}
              </div>

              {currentPlan && !isCancelledPlan && (
                <Button
                  variant="destructive"
                  className="rounded-full"
                  onClick={handleCancelSubscription}
                  disabled={isLoading}
                >
                  Cancel Subscription
                </Button>
              )}
            </div>
          </div>

          {/* Available Plans */}
          <h2 className="text-lg font-semibold mb-4">Available Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plans.filter((p) => p.id !== user?.subscription_plan).map((plan) => {
              const Icon = planIcons[plan.id] || Sparkles;
              const isUpgrade = (currentPlan?.price || 0) < plan.price;
              const isPopular = plan.id === "growth";

              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-2xl border-2 p-6 ${
                    isPopular ? "border-teal-500" : "border-slate-200"
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  {isPopular && (
                    <span className="inline-block px-3 py-1 rounded-full bg-teal-600 text-white text-xs font-semibold mb-4">
                      Most Popular
                    </span>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isPopular ? "bg-teal-100" : "bg-slate-100"
                    }`}>
                      <Icon className={`w-5 h-5 ${isPopular ? "text-teal-600" : "text-slate-600"}`} />
                    </div>
                    <div>
                      <h3 className="font-bold">{t(plan.name)}</h3>
                      <p className="text-slate-600 text-sm">
                        {`€${plan.price}${getPlanCycleLabel(plan)}`}
                      </p>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span className="text-slate-600">{t(feature)}</span>
                      </li>
                    ))}
                  </ul>

                  {paypalClientId ? (
                    <PayPalButtons
                      style={{ layout: "horizontal", label: isUpgrade ? "pay" : "subscribe", height: 40 }}
                      createSubscription={() => handleUpgrade(plan.id)}
                      onApprove={(data) => handlePayPalApprove(data, plan.id)}
                      onError={() => toast.error("Payment failed")}
                    />
                  ) : (
                    <p className="text-sm text-red-600">PayPal is temporarily unavailable. Please try again in a few minutes.</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Billing History */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-8">
            <h2 className="text-lg font-semibold mb-4">Billing History</h2>
            <div className="text-center py-8 text-slate-500">
              <p>No billing history yet</p>
            </div>
          </div>
        </main>
      </div>
  );

  if (!paypalClientId) {
    return subscriptionContent;
  }

  return (
    <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "EUR", vault: true, intent: "subscription" }}>
      {subscriptionContent}
    </PayPalScriptProvider>
  );
}
